<template>
  <section class="bg-white py-20 tablet:py-28">
    <div class="flex flex-col gap-20 tablet:gap-28">
      <!-- Feature 2: News Feed — copy LEFT, media RIGHT (matches design's desktop rendering; `reverse` in source only affects mobile stack order) -->
      <div class="container grid gap-10 tablet:grid-cols-2 tablet:items-center tablet:gap-16 [&>*]:min-w-0">
        <div class="flex flex-col gap-4">
          <i18n-t
            keypath="landing.featuresGrid.newsTitle"
            scope="global"
            tag="h2"
            class="text-3xl font-bold tracking-tight tablet:text-[44px] tablet:leading-[1.1]"
          >
            <template #br><br /></template>
            <template #accent
              ><span class="text-gold-primary-120">{{ t('landing.featuresGrid.newsTitleAccent') }}</span></template
            >
          </i18n-t>
          <p class="text-base text-black-80 tablet:text-lg">
            {{ t('landing.featuresGrid.newsDescription') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="tag in newsTags"
              :key="tag.id"
              class="inline-flex items-center rounded-full bg-gold-primary-10 px-3 py-1.5 text-xs font-medium text-gold-primary-120"
            >
              {{ tag.icon }} {{ t(`landing.featuresGrid.tags.${tag.id}`) }}
            </span>
          </div>
          <p class="mt-2 text-sm italic text-black-60">
            {{ t('landing.featuresGrid.newsTagline') }}
          </p>
        </div>
        <div>
          <div
            ref="feedMockRef"
            class="overflow-hidden rounded-2xl bg-white shadow-xl shadow-black/5 ring-1 ring-black/[0.06]"
          >
            <div class="flex items-center justify-between border-b border-black/[0.06] px-5 py-4">
              <div class="text-sm font-bold text-black-100">{{ t('landing.featuresGrid.feedTitle') }}</div>
              <div class="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600">
                <span class="feed-live-dot size-1.5 rounded-full bg-emerald-500"></span>
                <!-- inline-flex + gap restores the original 6px spacing between "Live ·", count and "new today". -->
                <i18n-t
                  keypath="landing.featuresGrid.feedLive"
                  scope="global"
                  tag="span"
                  class="inline-flex items-center gap-1.5"
                >
                  <template #count
                    ><span>{{ feedLiveCount }}</span></template
                  >
                </i18n-t>
              </div>
            </div>
            <div class="flex gap-1.5 overflow-x-auto border-b border-black/[0.06] px-4 py-3">
              <button
                v-for="chip in feedChips"
                :key="chip.key"
                class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors"
                :class="
                  activeFeedFilters.has(chip.key)
                    ? 'bg-gold-primary-120 text-white'
                    : 'bg-black/[0.04] text-black-80 hover:bg-black/[0.08]'
                "
                @click="toggleFeedFilter(chip.key)"
              >
                <span
                  v-if="chip.letter"
                  class="inline-flex size-4 items-center justify-center rounded-full text-[9px] font-bold"
                  :style="{ backgroundColor: chip.color, color: '#fff' }"
                >
                  {{ chip.letter }}
                </span>
                {{ chip.label }}
              </button>
            </div>
            <div class="divide-y divide-black/[0.05]">
              <div
                v-for="(item, i) in filteredFeed.slice(0, 5)"
                :key="item.dev + item.headline"
                :class="['flex items-start gap-3 px-4 py-3', feedIsNew && i === 0 ? 'feed-item-new' : '']"
              >
                <div
                  class="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold text-white"
                  :style="{ backgroundColor: devColor(item.dev) }"
                >
                  {{ item.logo }}
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2 text-[11px]">
                    <span class="font-semibold text-black-100">{{ item.name }}</span>
                    <span
                      class="rounded-full px-2 py-0.5 text-[10px] font-medium"
                      :style="{ backgroundColor: catBg(item.cat), color: catFg(item.cat) }"
                      >{{ t(`landing.featuresGrid.categories.${item.cat}`) }}</span
                    >
                  </div>
                  <div class="mt-0.5 truncate text-sm text-black-80">
                    {{ t(`landing.featuresGrid.headlines.${item.headline}`) }}
                  </div>
                  <span
                    v-if="item.attach"
                    class="mt-1 inline-flex items-center gap-1 text-[11px] text-black-60"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      class="size-3"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
                      />
                    </svg>
                    {{ t(`landing.featuresGrid.attachments.${item.attach}`) }}
                  </span>
                </div>
                <div class="shrink-0 text-[11px] text-black-60">{{ feedTime(item) }}</div>
              </div>
              <div
                v-if="filteredFeed.length === 0"
                class="px-6 py-6 text-center text-sm text-black-60"
              >
                {{ t('landing.featuresGrid.feedEmpty') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Feature 3: Vault — copy LEFT, media RIGHT (matches design; reverse is mobile-only) -->
      <div class="container grid gap-10 tablet:grid-cols-2 tablet:items-center tablet:gap-16 [&>*]:min-w-0">
        <div class="flex flex-col gap-4">
          <i18n-t
            keypath="landing.featuresGrid.vaultTitle"
            scope="global"
            tag="h2"
            class="text-3xl font-bold tracking-tight tablet:text-[44px] tablet:leading-[1.1]"
          >
            <template #br><br /></template>
            <template #accent
              ><span class="text-gold-primary-120">{{ t('landing.featuresGrid.vaultTitleAccent') }}</span></template
            >
          </i18n-t>
          <p class="text-base text-black-80 tablet:text-lg">
            {{ t(isUae ? 'landing.uae.features.vaultDescription' : 'landing.featuresGrid.vaultDescription') }}
          </p>
          <a class="inline-flex w-max items-center gap-1 text-sm font-semibold text-gold-primary-120 hover:underline"
            >{{ t('landing.featuresGrid.watchVault') }}
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >→</span
            ></a
          >
          <p class="mt-2 text-sm italic text-black-60">{{ t('landing.featuresGrid.vaultTagline') }}</p>
        </div>
        <div>
          <div class="flex flex-col gap-4">
            <div class="grid grid-cols-2 gap-3 tablet:gap-4">
              <!-- Tile 1: Elaris (in progress 65%) -->
              <NuxtLink
                :to="`/vault/${briefings[0].slug}`"
                class="group overflow-hidden rounded-2xl bg-white shadow-md shadow-black/5 ring-1 ring-black/[0.06] transition-shadow hover:shadow-lg"
              >
                <div class="relative aspect-[16/10] bg-[#FAF6EC]">
                  <img
                    :src="briefings[0].image"
                    :alt="t('landing.featuresGrid.briefingAlt', { title: briefings[0].title })"
                    loading="lazy"
                    class="size-full object-contain"
                  />
                  <div
                    class="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25"
                  >
                    <span class="flex size-11 items-center justify-center rounded-full bg-white/90 text-black-100">
                      <svg
                        viewBox="0 0 24 24"
                        class="size-4 fill-current"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </div>
                  <div class="absolute inset-x-0 bottom-0 h-1 bg-black/20">
                    <div
                      class="h-full bg-gold-primary-120"
                      style="width: 65%"
                    ></div>
                  </div>
                </div>
                <div class="flex items-center justify-between px-3 py-2 text-[11px] text-gold-primary-120">
                  <span class="inline-flex items-center gap-1">
                    <svg
                      viewBox="0 0 24 24"
                      class="size-3"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                      />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {{ t('landing.featuresGrid.minLeft', { count: 8 }) }}
                  </span>
                  <span class="inline-flex items-center gap-1 font-semibold">
                    <svg
                      viewBox="0 0 24 24"
                      class="size-3 fill-current"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    {{ t('landing.featuresGrid.resume') }}
                  </span>
                </div>
              </NuxtLink>
              <!-- Tile 2: Manchester (new) -->
              <NuxtLink
                :to="`/vault/${briefings[1].slug}`"
                class="group overflow-hidden rounded-2xl bg-white shadow-md shadow-black/5 ring-1 ring-black/[0.06] transition-shadow hover:shadow-lg"
              >
                <div class="relative aspect-[16/10] bg-[#FAF6EC]">
                  <img
                    :src="briefings[1].image"
                    :alt="t('landing.featuresGrid.briefingAlt', { title: briefings[1].title })"
                    loading="lazy"
                    class="size-full object-contain"
                  />
                  <div
                    class="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25"
                  >
                    <span class="flex size-11 items-center justify-center rounded-full bg-white/90 text-black-100">
                      <svg
                        viewBox="0 0 24 24"
                        class="size-4 fill-current"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </div>
                </div>
                <div class="flex items-center justify-between px-3 py-2 text-[11px] text-black-60">
                  <span class="inline-flex items-center gap-1">
                    <svg
                      viewBox="0 0 24 24"
                      class="size-3"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                      />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {{ t('landing.featuresGrid.minutes', { count: 15 }) }}
                  </span>
                  <span class="rounded-full bg-gold-primary-120 px-2 py-0.5 text-[10px] font-bold text-white">{{
                    t('landing.featuresGrid.new')
                  }}</span>
                </div>
              </NuxtLink>
              <!-- Tile 3: Seraph (in progress 30%) -->
              <NuxtLink
                :to="`/vault/${briefings[2].slug}`"
                class="group overflow-hidden rounded-2xl bg-white shadow-md shadow-black/5 ring-1 ring-black/[0.06] transition-shadow hover:shadow-lg"
              >
                <div class="relative aspect-[16/10] bg-[#FAF6EC]">
                  <img
                    :src="briefings[2].image"
                    :alt="t('landing.featuresGrid.briefingAlt', { title: briefings[2].title })"
                    loading="lazy"
                    class="size-full object-contain"
                  />
                  <div
                    class="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25"
                  >
                    <span class="flex size-11 items-center justify-center rounded-full bg-white/90 text-black-100">
                      <svg
                        viewBox="0 0 24 24"
                        class="size-4 fill-current"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </div>
                  <div class="absolute inset-x-0 bottom-0 h-1 bg-black/20">
                    <div
                      class="h-full bg-gold-primary-120"
                      style="width: 30%"
                    ></div>
                  </div>
                </div>
                <div class="flex items-center justify-between px-3 py-2 text-[11px] text-gold-primary-120">
                  <span class="inline-flex items-center gap-1">
                    <svg
                      viewBox="0 0 24 24"
                      class="size-3"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                      />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {{ t('landing.featuresGrid.minLeft', { count: 14 }) }}
                  </span>
                  <span class="inline-flex items-center gap-1 font-semibold">
                    <svg
                      viewBox="0 0 24 24"
                      class="size-3 fill-current"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    {{ t('landing.featuresGrid.resume') }}
                  </span>
                </div>
              </NuxtLink>
              <!-- Tile 4: AMRA -->
              <NuxtLink
                :to="`/vault/${briefings[3].slug}`"
                class="group overflow-hidden rounded-2xl bg-white shadow-md shadow-black/5 ring-1 ring-black/[0.06] transition-shadow hover:shadow-lg"
              >
                <div class="relative aspect-[16/10] bg-[#FAF6EC]">
                  <img
                    :src="briefings[3].image"
                    :alt="t('landing.featuresGrid.briefingAlt', { title: briefings[3].title })"
                    loading="lazy"
                    class="size-full object-contain"
                  />
                  <div
                    class="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25"
                  >
                    <span class="flex size-11 items-center justify-center rounded-full bg-white/90 text-black-100">
                      <svg
                        viewBox="0 0 24 24"
                        class="size-4 fill-current"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </div>
                </div>
                <div class="flex items-center justify-between px-3 py-2 text-[11px] text-black-60">
                  <span class="inline-flex items-center gap-1">
                    <svg
                      viewBox="0 0 24 24"
                      class="size-3"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                      />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {{ t('landing.featuresGrid.minutes', { count: 22 }) }}
                  </span>
                  <span>{{ t('landing.featuresGrid.addedDaysAgo', 3) }}</span>
                </div>
              </NuxtLink>
            </div>
            <NuxtLink
              :to="applyHref"
              @click="trackCtaClick('features')"
              class="inline-flex items-center justify-center gap-2 rounded-xl border border-gold-primary-100 bg-gold-primary-10 px-5 py-3 text-sm font-bold text-gold-primary-120 transition-colors hover:bg-gold-primary-20"
            >
              {{ t('landing.featuresGrid.watchAll') }}
              <svg
                viewBox="0 0 24 24"
                class="size-4 rtl:-scale-x-100"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </NuxtLink>
          </div>
        </div>
      </div>

      <!-- Feature 4: AI Presentations -->
      <div class="container grid gap-10 tablet:grid-cols-2 tablet:items-center tablet:gap-16 [&>*]:min-w-0">
        <div class="flex flex-col gap-4">
          <i18n-t
            keypath="landing.featuresGrid.aiTitle"
            scope="global"
            tag="h2"
            class="text-3xl font-bold tracking-tight tablet:text-[44px] tablet:leading-[1.1]"
          >
            <template #br><br /></template>
            <template #accent
              ><span class="text-gold-primary-120">{{ t('landing.featuresGrid.aiTitleAccent') }}</span></template
            >
          </i18n-t>
          <p class="text-base text-black-80 tablet:text-lg">
            {{ t(isUae ? 'landing.uae.features.aiDescription' : 'landing.featuresGrid.aiDescription') }}
          </p>
          <div class="flex flex-col gap-2">
            <div
              v-for="item in aiBullets"
              :key="item"
              class="flex items-start gap-2 text-sm text-black-80"
            >
              <svg
                viewBox="0 0 24 24"
                class="mt-0.5 size-4 shrink-0 fill-gold-primary-120"
              >
                <path d="M10 15.2L6.8 12l-1.4 1.4L10 18l10-10-1.4-1.4z" />
              </svg>
              <span>{{ aiBulletText(item) }}</span>
            </div>
          </div>
          <p class="mt-2 text-sm font-bold italic text-black-60">{{ t('landing.featuresGrid.aiTagline') }}</p>
        </div>
        <div>
          <div class="flex flex-col items-center gap-4">
            <!-- Prompt card -->
            <div class="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl shadow-black/5 ring-1 ring-black/[0.06]">
              <div class="flex items-center gap-3">
                <div
                  class="flex size-9 items-center justify-center rounded-xl bg-gold-primary-10 text-gold-primary-120"
                >
                  <svg
                    viewBox="0 0 24 24"
                    class="size-4 fill-current"
                  >
                    <path d="M12 2l2.09 5.26L19.5 8l-4 3.9.94 5.6L12 14.77 7.56 17.5l.94-5.6-4-3.9 5.41-.74z" />
                  </svg>
                </div>
                <div>
                  <div class="text-sm font-bold text-black-100">
                    {{ t('landing.featuresGrid.generatePresentation') }}
                  </div>
                  <div class="text-xs text-black-60">{{ t('landing.featuresGrid.forClient') }}</div>
                </div>
              </div>
              <div class="mt-4 flex flex-col gap-1.5 text-xs">
                <div class="flex items-center justify-between rounded-lg bg-bg px-3 py-2">
                  <span class="text-black-60">{{ t('landing.featuresGrid.project') }}</span>
                  <span class="font-semibold text-black-100">Elaris Sky · Object 1</span>
                </div>
                <div class="flex items-center justify-between rounded-lg bg-bg px-3 py-2">
                  <span class="text-black-60">{{ t('landing.featuresGrid.language') }}</span>
                  <span class="font-semibold text-black-100">{{ t('landing.featuresGrid.languageValue') }}</span>
                </div>
                <div class="flex items-center justify-between rounded-lg bg-bg px-3 py-2">
                  <span class="text-black-60">{{ t('landing.featuresGrid.unit') }}</span>
                  <span class="font-semibold text-black-100">2BR · 1,248 sqft · 2.4M</span>
                </div>
              </div>
              <div class="mt-4 flex items-center justify-between">
                <NuxtLink
                  :to="applyHref"
                  @click="trackCtaClick('features')"
                  class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-gold-primary-100 to-gold-primary-120 px-4 py-2 text-sm font-bold text-black-100 transition-opacity hover:opacity-90"
                >
                  <svg
                    viewBox="0 0 24 24"
                    class="size-3.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M12 2l2.09 5.26L19.5 8l-4 3.9.94 5.6L12 14.77 7.56 17.5l.94-5.6-4-3.9 5.41-.74z" />
                  </svg>
                  {{ t('landing.featuresGrid.generate') }}
                </NuxtLink>
                <div class="text-xs text-black-60">~28s</div>
              </div>
            </div>

            <!-- Flow dot -->
            <div class="flex flex-col items-center">
              <div class="h-6 w-px bg-gold-primary-100"></div>
              <div class="size-2 rounded-full bg-gold-primary-120"></div>
              <div class="h-6 w-px bg-gold-primary-100"></div>
            </div>

            <!-- Output cover -->
            <div class="relative w-full max-w-md">
              <div
                class="pres-stack-edge-2 absolute inset-x-6 -bottom-3 h-8 rounded-2xl bg-white ring-1 ring-black/[0.05]"
              ></div>
              <div
                class="pres-stack-edge-1 absolute inset-x-3 -bottom-1.5 h-8 rounded-2xl bg-white ring-1 ring-black/[0.06]"
              ></div>
              <div class="relative overflow-hidden rounded-2xl bg-[#0B0E1A] p-6 text-white shadow-xl shadow-black/10">
                <div class="flex items-center justify-between text-[10px]">
                  <span class="inline-flex items-center gap-1.5">
                    <span class="size-1.5 rounded-full bg-gold-primary-100"></span>
                    <span class="uppercase tracking-widest text-white/70">{{
                      t('landing.featuresGrid.confidential')
                    }}</span>
                  </span>
                  <span class="text-white/60">{{ t('landing.featuresGrid.reference', { ref: '2026-0412' }) }}</span>
                </div>
                <div class="relative mt-4 flex h-[220px] items-end justify-center">
                  <svg
                    viewBox="0 0 170 230"
                    fill="none"
                    class="h-full"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient
                        id="towerFill"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0"
                          stop-color="#FFB648"
                          stop-opacity="0.28"
                        />
                        <stop
                          offset="1"
                          stop-color="#FFB648"
                          stop-opacity="0.04"
                        />
                      </linearGradient>
                      <linearGradient
                        id="towerStroke"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0"
                          stop-color="#FFB648"
                          stop-opacity="0.95"
                        />
                        <stop
                          offset="1"
                          stop-color="#FFB648"
                          stop-opacity="0.35"
                        />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 58 44 L 58 190 L 70 190 L 70 40 Z"
                      fill="url(#towerFill)"
                      stroke="url(#towerStroke)"
                      stroke-width="0.6"
                    />
                    <path
                      d="M 74 14 L 74 190 L 118 190 L 118 18 Z"
                      fill="url(#towerFill)"
                      stroke="url(#towerStroke)"
                      stroke-width="0.8"
                    />
                    <path
                      d="M 74 14 L 96 6 L 118 18"
                      fill="none"
                      stroke="url(#towerStroke)"
                      stroke-width="0.8"
                      stroke-linejoin="round"
                    />
                    <g
                      stroke="rgba(255,182,72,0.45)"
                      stroke-width="0.4"
                    >
                      <line
                        x1="82"
                        y1="24"
                        x2="82"
                        y2="186"
                      />
                      <line
                        x1="90"
                        y1="24"
                        x2="90"
                        y2="186"
                      />
                      <line
                        x1="96"
                        y1="18"
                        x2="96"
                        y2="186"
                      />
                      <line
                        x1="102"
                        y1="24"
                        x2="102"
                        y2="186"
                      />
                      <line
                        x1="110"
                        y1="24"
                        x2="110"
                        y2="186"
                      />
                    </g>
                    <g
                      stroke="rgba(255,182,72,0.28)"
                      stroke-width="0.3"
                    >
                      <line
                        x1="74"
                        y1="40"
                        x2="118"
                        y2="40"
                      />
                      <line
                        x1="74"
                        y1="60"
                        x2="118"
                        y2="60"
                      />
                      <line
                        x1="74"
                        y1="80"
                        x2="118"
                        y2="80"
                      />
                      <line
                        x1="74"
                        y1="100"
                        x2="118"
                        y2="100"
                      />
                      <line
                        x1="74"
                        y1="120"
                        x2="118"
                        y2="120"
                      />
                      <line
                        x1="74"
                        y1="140"
                        x2="118"
                        y2="140"
                      />
                      <line
                        x1="74"
                        y1="160"
                        x2="118"
                        y2="160"
                      />
                      <line
                        x1="74"
                        y1="180"
                        x2="118"
                        y2="180"
                      />
                    </g>
                    <g
                      stroke="rgba(255,182,72,0.35)"
                      stroke-width="0.3"
                    >
                      <line
                        x1="64"
                        y1="48"
                        x2="64"
                        y2="186"
                      />
                      <line
                        x1="58"
                        y1="80"
                        x2="70"
                        y2="80"
                      />
                      <line
                        x1="58"
                        y1="120"
                        x2="70"
                        y2="120"
                      />
                      <line
                        x1="58"
                        y1="160"
                        x2="70"
                        y2="160"
                      />
                    </g>
                    <path
                      d="M 124 70 L 124 190 L 134 190 L 134 66 Z"
                      fill="url(#towerFill)"
                      stroke="url(#towerStroke)"
                      stroke-width="0.5"
                      opacity="0.8"
                    />
                    <rect
                      x="50"
                      y="190"
                      width="90"
                      height="18"
                      fill="url(#towerFill)"
                      opacity="0.25"
                    />
                  </svg>
                </div>
                <div class="mt-2 h-px bg-gold-primary-100/60"></div>
                <div
                  class="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent"
                ></div>
                <div class="relative mt-4 flex flex-col gap-3">
                  <div>
                    <div class="font-display text-2xl font-bold">Elaris Sky</div>
                    <div class="text-sm text-white/70">Object 1 · Business Bay</div>
                  </div>
                  <div class="flex items-center gap-2 border-t border-white/10 pt-3">
                    <div class="size-8 rounded-full bg-gradient-to-br from-gold-primary-100 to-gold-primary-120"></div>
                    <div>
                      <div class="text-xs font-semibold text-white">Yousef Al Mansouri</div>
                      <div class="text-[10px] text-white/60">
                        {{ isUae ? MOCK_AGENT_PHONE : t('landing.featuresGrid.advisorRole', { number: '67423' }) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="absolute -end-2 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg"
              >
                <span class="size-1.5 rounded-full bg-white"></span>
                {{ t('landing.featuresGrid.generatedIn') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Feature 5: Income Streams -->
      <div class="container grid gap-10 tablet:grid-cols-2 tablet:items-center tablet:gap-16 [&>*]:min-w-0">
        <div class="flex flex-col gap-4">
          <i18n-t
            keypath="landing.featuresGrid.incomeTitle"
            scope="global"
            tag="h2"
            class="text-3xl font-bold tracking-tight tablet:text-[44px] tablet:leading-[1.1]"
          >
            <template #br><br /></template>
            <template #accent
              ><span class="text-gold-primary-120">{{ t('landing.featuresGrid.incomeTitleAccent') }}</span></template
            >
          </i18n-t>
          <p class="text-base font-bold text-black-100 tablet:text-lg">
            {{ t('landing.featuresGrid.incomeLead') }}
          </p>
          <i18n-t
            keypath="landing.featuresGrid.incomeDescription"
            scope="global"
            tag="p"
            class="text-base text-black-80 tablet:text-lg"
          >
            <template #emphasis
              ><strong class="font-bold text-black-100">{{
                t('landing.featuresGrid.incomeEmphasis')
              }}</strong></template
            >
          </i18n-t>
        </div>
        <div>
          <div class="relative mx-auto aspect-square w-full max-w-[520px]">
            <svg
              class="absolute inset-0 size-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient
                  id="streamLine"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop
                    offset="0"
                    stop-color="#EDA436"
                    stop-opacity="0.55"
                  />
                  <stop
                    offset="1"
                    stop-color="#EDA436"
                    stop-opacity="0.1"
                  />
                </linearGradient>
              </defs>
              <g
                stroke="url(#streamLine)"
                stroke-width="0.4"
                fill="none"
              >
                <line
                  x1="50"
                  y1="50"
                  x2="50"
                  y2="12"
                />
                <line
                  x1="50"
                  y1="50"
                  x2="76.87"
                  y2="23.13"
                />
                <line
                  x1="50"
                  y1="50"
                  x2="88"
                  y2="50"
                />
                <line
                  x1="50"
                  y1="50"
                  x2="76.87"
                  y2="76.87"
                />
                <line
                  x1="50"
                  y1="50"
                  x2="50"
                  y2="88"
                />
                <line
                  x1="50"
                  y1="50"
                  x2="23.13"
                  y2="76.87"
                />
                <line
                  x1="50"
                  y1="50"
                  x2="12"
                  y2="50"
                />
                <line
                  x1="50"
                  y1="50"
                  x2="23.13"
                  y2="23.13"
                />
              </g>
              <circle
                cx="50"
                cy="50"
                r="38"
                fill="none"
                stroke="rgba(237,164,54,0.18)"
                stroke-width="0.3"
                stroke-dasharray="0.8 1.2"
              />
            </svg>

            <!-- Center hub -->
            <div
              class="absolute left-1/2 top-1/2 flex size-[38%] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-white text-center shadow-xl shadow-black/5 ring-1 ring-gold-primary-100"
            >
              <div class="text-[10px] font-semibold uppercase tracking-widest text-gold-primary-120">
                {{ t('landing.featuresGrid.hubLabel') }}
              </div>
              <div class="mt-1 text-base font-black text-black-100 tablet:text-xl">AED 2.4M</div>
              <div class="text-[10px] text-black-60">{{ t('landing.featuresGrid.hubPurchase') }}</div>
              <div
                class="mt-1 rounded-full bg-gold-primary-10 px-2 py-0.5 text-[10px] font-semibold text-gold-primary-120"
              >
                {{ t('landing.featuresGrid.hubServices', 8) }}
              </div>
            </div>

            <!-- Chips -->
            <div
              v-for="chip in streamChips"
              :key="chip.id"
              class="absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-xl bg-white px-2.5 py-1.5 shadow-md shadow-black/5 ring-1 ring-black/[0.06]"
              :style="{ left: chip.left, top: chip.top }"
            >
              <div class="text-base">{{ chip.icon }}</div>
              <div>
                <div class="text-[11px] font-semibold text-black-100">
                  {{ t(`landing.featuresGrid.streams.${chip.id}`) }}
                </div>
                <div class="text-[9px] font-medium text-gold-primary-120">
                  {{ t('landing.featuresGrid.streamShare') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Totals -->
          <div class="mt-8 grid grid-cols-3 gap-3 rounded-2xl border border-black/[0.06] bg-white p-4">
            <div class="text-center">
              <div class="text-2xl font-black text-black-100">1</div>
              <div class="text-[10px] uppercase tracking-wider text-black-60">
                {{ t('landing.featuresGrid.totalSale') }}
              </div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-black text-gold-primary-120">8</div>
              <div class="text-[10px] uppercase tracking-wider text-black-60">
                {{ t('landing.featuresGrid.totalStreams') }}
              </div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-black text-gold-primary-120">30%</div>
              <div class="text-[10px] uppercase tracking-wider text-black-60">
                {{ t('landing.featuresGrid.totalEvery') }}
              </div>
            </div>
          </div>

          <i18n-t
            keypath="landing.featuresGrid.incomeClosing"
            scope="global"
            tag="p"
            class="mt-5 text-center text-base text-black-80 tablet:text-lg"
          >
            <template #br><br /></template>
            <template #accent
              ><span class="font-bold text-gold-primary-120">{{
                t('landing.featuresGrid.incomeClosingAccent')
              }}</span></template
            >
          </i18n-t>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core';
import type { SiteMarket } from '~/constants/market';

const { t } = useI18n();
const { market: marketProp } = defineProps<{ market?: SiteMarket }>();
const { track } = useLandingPage();
// /uae: no membership wording, no RERA number on the mock slide (name + phone only).
const isUae = computed(() => (marketProp ?? track.value) === 'uae');
const MOCK_AGENT_PHONE = '+971 50 123 4567';

const aiBulletText = (item: (typeof aiBullets)[number]) =>
  isUae.value && item === 'branding'
    ? t('landing.uae.features.aiBranding')
    : t(`landing.featuresGrid.aiBullets.${item}`);

const { signupHref: applyHref } = useSignupHref();
const { trackCtaClick } = useLandingTracking();

// Developer Vault briefings — each tile links to /vault/[slug] which shows
// a blurred paywall teaser for unauthenticated visitors.
const briefings = [
  { slug: 'elaris-sky-object-1', title: 'Elaris Sky Object 1', image: '/images/agents/briefing-elaris.png' },
  {
    slug: 'manchester-city-residences',
    title: 'Manchester City Residences',
    image: '/images/agents/briefing-manchester.png',
  },
  { slug: 'seraph-tower', title: 'Seraph by Wadan Developments', image: '/images/agents/briefing-seraph.png' },
  { slug: 'amra-pricing-deep-dive', title: 'AMRA pricing deep-dive', image: '/images/agents/briefing-amra.png' },
] as const;

// ========= Shared data =========
// Keys under `landing.featuresGrid.aiBullets`.
const aiBullets = ['oneTouch', 'branding', 'localize', 'editPrice', 'share'] as const;

// Keys under `landing.featuresGrid.tags`.
const newsTags = [
  { id: 'launches', icon: '🚀' },
  { id: 'commission', icon: '💰' },
  { id: 'paymentPlans', icon: '📋' },
  { id: 'priceChanges', icon: '💵' },
  { id: 'handover', icon: '🏗️' },
  { id: 'preLaunch', icon: '⚡' },
] as const;

// `id` → name at `landing.featuresGrid.streams.<id>`.
const streamChips = [
  { id: 'mortgage', icon: '🏦', left: '50%', top: '12%' },
  { id: 'goldenVisa', icon: '🛂', left: '76.87%', top: '23.13%' },
  { id: 'snagging', icon: '🔍', left: '88%', top: '50%' },
  { id: 'interiors', icon: '🎨', left: '76.87%', top: '76.87%' },
  { id: 'holidayHomes', icon: '🏝️', left: '50%', top: '88%' },
  { id: 'companySetup', icon: '🏢', left: '23.13%', top: '76.87%' },
  { id: 'concierge', icon: '🔔', left: '12%', top: '50%' },
  { id: 'crypto', icon: '₿', left: '23.13%', top: '23.13%' },
] as const;

// ========= Row 2: News feed =========
// Mock feed rows: `cat` → `landing.featuresGrid.categories.<cat>`, `headline` →
// `landing.featuresGrid.headlines.<headline>`, `attach` → `…attachments.<attach>`;
// age is rendered from `ago` + `unit` ('m' minutes / 'h' hours).
type FeedItem = {
  dev: string;
  name: string;
  logo: string;
  cat: string;
  headline: string;
  ago: number;
  unit: 'm' | 'h';
  attach: string | null;
};

const feedItems = ref<FeedItem[]>([
  {
    dev: 'emaar',
    name: 'Emaar',
    logo: 'E',
    cat: 'launch',
    headline: 'marinaBay',
    ago: 2,
    unit: 'm',
    attach: 'factSheet',
  },
  {
    dev: 'damac',
    name: 'DAMAC',
    logo: 'D',
    cat: 'commission',
    headline: 'lagoonsViews',
    ago: 14,
    unit: 'm',
    attach: null,
  },
  { dev: 'sobha', name: 'Sobha', logo: 'S', cat: 'payment', headline: 'sobhaOne', ago: 32, unit: 'm', attach: 'plan' },
  {
    dev: 'meraas',
    name: 'Meraas',
    logo: 'M',
    cat: 'prelaunch',
    headline: 'bluewaters',
    ago: 1,
    unit: 'h',
    attach: null,
  },
  { dev: 'nakheel', name: 'Nakheel', logo: 'N', cat: 'price', headline: 'palmBeach', ago: 2, unit: 'h', attach: null },
  {
    dev: 'emaar',
    name: 'Emaar',
    logo: 'E',
    cat: 'handover',
    headline: 'creekHorizon',
    ago: 3,
    unit: 'h',
    attach: null,
  },
  { dev: 'aldar', name: 'Aldar', logo: 'A', cat: 'launch', headline: 'yasPark', ago: 4, unit: 'h', attach: 'brochure' },
  { dev: 'damac', name: 'DAMAC', logo: 'D', cat: 'payment', headline: 'cavalli', ago: 5, unit: 'h', attach: null },
  { dev: 'sobha', name: 'Sobha', logo: 'S', cat: 'price', headline: 'hartland', ago: 6, unit: 'h', attach: null },
  {
    dev: 'meraas',
    name: 'Meraas',
    logo: 'M',
    cat: 'commission',
    headline: 'cityWalk',
    ago: 7,
    unit: 'h',
    attach: null,
  },
  {
    dev: 'nakheel',
    name: 'Nakheel',
    logo: 'N',
    cat: 'launch',
    headline: 'como',
    ago: 9,
    unit: 'h',
    attach: 'factSheet',
  },
  { dev: 'aldar', name: 'Aldar', logo: 'A', cat: 'prelaunch', headline: 'saadiyat', ago: 11, unit: 'h', attach: null },
]);

const feedTime = (item: FeedItem) =>
  t(item.unit === 'm' ? 'landing.featuresGrid.minutesAgo' : 'landing.featuresGrid.hoursAgo', { count: item.ago });

const feedChips = computed(() => [
  { key: 'all', label: t('landing.featuresGrid.allDevelopers'), letter: '', color: '' },
  { key: 'emaar', label: 'Emaar', letter: 'E', color: '#1E40AF' },
  { key: 'damac', label: 'DAMAC', letter: 'D', color: '#059669' },
  { key: 'sobha', label: 'Sobha', letter: 'S', color: '#7C3AED' },
  { key: 'nakheel', label: 'Nakheel', letter: 'N', color: '#DC2626' },
  { key: 'meraas', label: 'Meraas', letter: 'M', color: '#EA580C' },
  { key: 'aldar', label: 'Aldar', letter: 'A', color: '#0891B2' },
]);

const activeFeedFilters = ref<Set<string>>(new Set(['all']));
const feedLiveCount = ref(12);
const feedIsNew = ref(false);
const feedMockRef = ref<HTMLElement | null>(null);

const devColors: Record<string, string> = {
  emaar: '#1E40AF',
  damac: '#059669',
  sobha: '#7C3AED',
  nakheel: '#DC2626',
  meraas: '#EA580C',
  aldar: '#0891B2',
};
const devColor = (dev: string) => devColors[dev] ?? '#6B7280';

const catPalette: Record<string, { bg: string; fg: string }> = {
  launch: { bg: '#FEF3C7', fg: '#92400E' },
  commission: { bg: '#DCFCE7', fg: '#166534' },
  payment: { bg: '#DBEAFE', fg: '#1E40AF' },
  price: { bg: '#FEE2E2', fg: '#991B1B' },
  handover: { bg: '#F3E8FF', fg: '#6B21A8' },
  prelaunch: { bg: '#FFE4E6', fg: '#9F1239' },
};
const catBg = (cat: string) => catPalette[cat]?.bg ?? '#F3F4F6';
const catFg = (cat: string) => catPalette[cat]?.fg ?? '#374151';

const filteredFeed = computed(() => {
  const active = activeFeedFilters.value;
  if (active.has('all') || active.size === 0) return feedItems.value;
  return feedItems.value.filter((it) => active.has(it.dev));
});

const toggleFeedFilter = (dev: string) => {
  const next = new Set(activeFeedFilters.value);
  if (dev === 'all') {
    next.clear();
    next.add('all');
  } else {
    next.delete('all');
    if (next.has(dev)) next.delete(dev);
    else next.add(dev);
    if (next.size === 0) next.add('all');
  }
  activeFeedFilters.value = next;
};

let feedTimer: ReturnType<typeof setInterval> | null = null;
const startFeed = () => {
  if (feedTimer) return;
  feedTimer = setInterval(() => {
    const arr = feedItems.value.slice();
    const last = arr.pop();
    if (last) {
      arr.unshift(last);
      feedItems.value = arr;
      feedLiveCount.value = Math.min(feedLiveCount.value + 1, 99);
      feedIsNew.value = true;
      setTimeout(() => (feedIsNew.value = false), 400);
    }
  }, 4500);
};
const stopFeed = () => {
  if (feedTimer) {
    clearInterval(feedTimer);
    feedTimer = null;
  }
};

useIntersectionObserver(feedMockRef, ([entry]) => (entry.isIntersecting ? startFeed() : stopFeed()), {
  threshold: 0.25,
});

onBeforeUnmount(() => {
  stopFeed();
});
</script>

<style scoped lang="scss">
.feed-item-new {
  animation: slideIn 380ms ease;
}
@keyframes slideIn {
  from {
    transform: translateY(-8px);
    opacity: 0;
  }
  to {
    transform: none;
    opacity: 1;
  }
}
</style>
